import { AlertTitle, Box, Button, Card, CardContent, Grid, styled, TextareaAutosize, TextField, Typography, useTheme } from '@mui/material';
import { FC, useContext, useState, } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../contexts/auth/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-duotone-svg-icons';
import { faWrench } from '@fortawesome/pro-duotone-svg-icons';
import useGetEmailTemplate from '../../hooks/data/useGetEmailTemplate';
import AlertContext from '../../contexts/styles/AlertContext';
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from '../../constants/styles/AlertConstants';
import { PostCommunityGroupInvitePeople, Species } from '../../api/dtos/dtos';
import ServiceStackClientContext from '../../contexts/api/ServiceStackContext';
import MapContext from '../../contexts/data/MapContext';
import { SpeciesContext } from '../../contexts/data/SpeciesContext';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

const ContentWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const InviteGroupMembersComponent: FC = () => {
    let navigate = useNavigate();
    const theme = useTheme();

    //
    // -- Params
    const { species: speciesName } = useParams();
    const { species: { species } } = useContext(SpeciesContext);

    //
    // -- States --
    const [toInput, setToInput] = useState<string>("");
    const [subjectInput, setSubjectInput] = useState<string>("Invitation to join a FeralScan group");
    const [showGenericMessage, setShowGenericMessage] = useState<boolean>(false);
    const [personalisedMessageInput, setPersonalisedMessageInput] = useState<string>("");
    const [disabled, setDisabled] = useState<boolean>(false);

    //
    // -- Context --
    const client = useContext(ServiceStackClientContext);
    const { addAlert } = useContext(AlertContext);
    const { filter } = useContext(MapContext);

    const handleToInputChange = (event: any) => {
        setToInput(event.target.value);
    };

    const handleSubjectInputChange = (event: any) => {
        setSubjectInput(event.target.value);
    };

    const handlePersonalisedMessageInputChange = (event: any) => {
        setPersonalisedMessageInput(event.target.value);
    };

    function delimitedStringToArray(str: string, delims: string[]): string[] {
        // Replace multiple spaces with a single space
        str = str.replace(/\s+/g, ' ');
    
        // Get rid of leading/trailing spaces
        delims.forEach(delim => {
            str = str.replace(new RegExp(`\\${delim} `, 'g'), delim);
            str = str.replace(new RegExp(` ${delim}`, 'g'), delim);
        });
    
        // Split apart
        let strArray = str.split(new RegExp(`[${delims.join('')}]`, 'g')).filter(s => s.length > 0);
    
        // Make distinct (to an extent)
        strArray = Array.from(new Set(strArray));
    
        return strArray;
    }
    

    const handleSubmit = async (to: string, subject: string, personalisedMessage: string, communityGroupId: number, species: Species) => {
        setDisabled(true);
        let failed = false;

        // Validation
        if (to === ""){
            failed = true;
            addAlert({
                content: <AlertTitle>
                            Please enter at least one "To" address.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
        if (subject === ""){
            failed = true;
            addAlert({
                content: <AlertTitle>
                            Please enter a subject.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }

        let toArray = delimitedStringToArray(to, [',', ';', '\r', '\n']);

        try {
            await client.post(new PostCommunityGroupInvitePeople({
                communityGroupId,
                emailTo: toArray,
                subject: subject,
                personalisedMessage: personalisedMessage,
                autoAccept: false,
                species: species 
            }));
        }
        catch (error) {
            failed = true;
            console.error("Error sending email:", error);
            addAlert({
                content: <AlertTitle>
                            An error occurred while sending the email.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
        finally {
            if (!failed){
                addAlert({
                    content: <AlertTitle>
                                Thank you, your group invitations have been successfully sent.
                            </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });
            }
            setToInput("");
            setSubjectInput("Invitation to join a FeralScan group");
            setPersonalisedMessageInput("");
            setDisabled(false);
        }

    };

    // Get Email template
    const { data: template }  = useGetEmailTemplate(
        { name: "CommunityGroupInviteNotReg" }, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    return (
        <Box>
            <ContentWrapper>
                {/* Header */}
                <Typography fontSize={32} color="#FFFFFF" sx={{paddingTop: '15px'}}>Manage Group Members</Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        color: 'white'
                    }}
                >
                    <Button
                        variant="text"
                        sx={{
                            color: 'white',
                            fontSize: '0.65rem',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.15)'
                            }
                        }}
                        startIcon={
                            <FontAwesomeIcon
                                icon={faArrowLeft} 
                                style={{ 
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    paddingRight: '3px' 
                                }}
                            />
                        }
                        onClick={() => navigate("/" + speciesName + "/datamanagement")}
                    >
                        Back to Data Management
                    </Button>
                </Box>

                <Card variant='outlined' sx={{marginTop: '20px', marginBottom: '20px'}}>
                    <CardContent>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12}>
                                <Button 
                                    variant="text" 
                                    startIcon={<FontAwesomeIcon icon={faWrench} />}
                                    onClick={() => navigate("/" + speciesName + "/managegroupmembers")}
                                >
                                    Manage Group Members
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography paddingLeft="5px" paddingTop="10px" fontSize={24}>
                                        Invite Group Members
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography paddingLeft="5px">
                                        This page allows you to invite people to join your private group in FeralScan. You can invite people individually, or several people simultaneously. Type the email address(es) for each person into the text box below, separating each email address with a comma (e.g. firstname.surname@hotmail.com, firstname.surname@yahoo.com etc). We recommend you compose a short personal message and include your name, to be sent with your group invitation email.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography paddingLeft="5px">
                                        Once each person receives your email invitation, they can Accept the invitation by logging into the FeralScan website using a link in the email invitation. If they do not receive the email, the next time they Login to the FeralScan website, their invitation will be waiting for them to Accept or Reject.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2} marginTop="30px">
                                <Typography align="right">To:</Typography>
                            </Grid>
                            <Grid item xs={8}  marginTop="30px">
                                <TextField 
                                    id="outlined-to" 
                                    variant="outlined"
                                    sx={{ width: '100%' }}
                                    value={toInput}
                                    onChange={handleToInputChange} />
                            </Grid>
                            <Grid item xs={2} marginTop="30px"></Grid>
                            <Grid item xs={2} >
                                <Typography align="right">Subject:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <TextField 
                                    id="outlined-subject" 
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: '100%' }}
                                    value={subjectInput}
                                    onChange={handleSubjectInputChange} />
                            </Grid>
                            <Grid item xs={2}></Grid>
                            <Grid item xs={2}></Grid>
                            <Grid item xs={8}>
                                <Typography paddingLeft="5px">
                                    Your email message will contain a <Link onClick={() => {setShowGenericMessage(!showGenericMessage)}} to={''} style={{ color: theme.palette.primary.main }}>generic message</Link> and can also include a personalised message as specified below.
                                </Typography>
                            </Grid>
                            <Grid item xs={2}></Grid>
                            <Grid item xs={2} sx={{ display: showGenericMessage ? 'block' : 'none' }}></Grid>
                            <Grid item xs={8} sx={{ display: showGenericMessage ? 'block' : 'none' }}>
                                <Box sx={{ backgroundColor: theme.palette.grey[300], padding: '10px' }}>
                                    { parse(DOMPurify.sanitize(template?.htmlText ?? "")) }
                                </Box>
                            </Grid>
                            <Grid item xs={2} sx={{ display: showGenericMessage ? 'block' : 'none' }}></Grid>
                            <Grid item xs={2} >
                                <Typography align="right">Personalised Message:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <TextareaAutosize
                                    style={{ width: '100%', minHeight: '150px' }}  
                                    id="outlined-basic" 
                                    value={personalisedMessageInput}
                                    onChange={handlePersonalisedMessageInputChange} />
                            </Grid>
                            <Grid item xs={2}></Grid>
                            <Grid item xs={4}></Grid>
                            <Grid item xs={4}>
                                <Button 
                                    variant="contained"
                                    sx={{ width: "100%" }}
                                    onClick={() => handleSubmit(toInput, subjectInput, personalisedMessageInput, filter.bySubmitter.communityGroupId ?? -1, species)}
                                    disabled={disabled}
                                >
                                    Send Email    
                                </Button>
                            </Grid>
                            <Grid item xs={4} ></Grid>
                        </Grid>

                        <Box height="20px" />
                    </CardContent>
                </Card>
                
            </ContentWrapper>
        </Box>
    )
}

const InviteGroupMembersPage: FC = () => {
    const { user } = useContext(AuthContext);
    const { species: speciesName } = useParams();

    if (!user) {
        return <Navigate to={"/" + speciesName + "/map"} replace />
    }
    return (<InviteGroupMembersComponent />)
}

export default InviteGroupMembersPage
